import axios from 'axios';
import API_CONFIG from '../config/api.js';

/**
 * System Status Service (PMS)
 *
 * Reads the backend kill switch. `/api/system/status` is deliberately exempt
 * from the switch, so it stays reachable while the PMS itself is paused.
 */

export const SURFACE = 'pms';

/** Identifies this app to the backend so it pauses the right surface. */
export const CLIENT_APP_HEADER = 'pms';

/** Dispatched whenever an API call is rejected with a 503 SERVICE_PAUSED. */
export const SERVICE_PAUSED_EVENT = 'osho:service-paused';

const statusClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 12000,
  headers: { ...API_CONFIG.DEFAULT_HEADERS, 'X-Client-App': CLIENT_APP_HEADER },
});

/**
 * Current maintenance state for the PMS surface.
 * Returns null when the check itself fails, so a flaky connection is never
 * mistaken for an intentional shutdown.
 */
export const fetchSystemStatus = async () => {
  try {
    const response = await statusClient.get('/api/system/status');
    const data = response?.data?.data;
    if (!data) return null;
    return {
      maintenance: data[SURFACE] || null,
      bookingEngine: data.bookingEngine || null,
      contact: data.contact || data[SURFACE]?.contact || null,
      checkedAt: data.checkedAt,
    };
  } catch (error) {
    const payload = error?.response?.data;
    if (payload?.maintenance) {
      return {
        maintenance: payload.maintenance,
        bookingEngine: null,
        contact: payload.maintenance.contact,
        checkedAt: null,
      };
    }
    console.warn('System status check failed:', error?.message || error);
    return null;
  }
};

/**
 * Broadcast a 503 SERVICE_PAUSED response so the gate switches to the
 * maintenance screen without waiting for the next poll.
 */
export const announceServicePaused = (maintenance) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SERVICE_PAUSED_EVENT, { detail: maintenance || null }));
};

const systemStatusService = {
  fetchSystemStatus,
  announceServicePaused,
  SERVICE_PAUSED_EVENT,
  CLIENT_APP_HEADER,
};

export default systemStatusService;
